import { ESdChangeReason } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { IWithFormProp, WithForm } from "../../../../../shared/hoc/with-form";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import "./data-key-editor.scss";

interface IDataKeyTextField {
  key: string;
  text: string;
}

export interface IDataKeyTextFieldListState {
  fieldList: IDataKeyTextField[];
}

export interface IDataKeyTextFieldListProps {
  value: string;
  onChange?: (value: string) => void;
  handleClickOnCancel?: () => void;
}

class DataKeyTextFieldList extends PureSdComponent<
  IDataKeyTextFieldListProps & IWithSDProps & IWithContextProp & IWithFormProp<any>,
  IDataKeyTextFieldListState
> {
  private _isMounted = false;

  constructor(
    props: IDataKeyTextFieldListProps & IWithSDProps & IWithContextProp & IWithFormProp<IDataKeyTextFieldListProps>
  ) {
    super(props);
    this.state = {
      fieldList: []
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadOptionListForm();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleFinish = (formData: any) => {
    if (!this._isMounted) return;

    const data = this.state.fieldList;
    if (data.length !== _.uniqBy(data, "key").length) {
      KMainFunctions.displayWarningNotification("Field Key is duplicated, key must be unique. Please check again.");
      return;
    }
    const result = JSON.stringify(data);
    if (this.props.onChange) {
      this.props.onChange(result);
    }
  };

  loadOptionListForm = () => {
    if (!this._isMounted) return;

    let fieldList: IDataKeyTextField[] = [];
    if (this.props.value && this.props.value.length > 0) {
      try {
        fieldList = JSON.parse(this.props.value);
      } catch (error) {}
    }
    this.setState({ fieldList });
  };

  handleAddField = () => {
    if (!this._isMounted) return;

    this.setState((prevState) => ({
      fieldList: [...prevState.fieldList, { key: "", text: "" }]
    }));
  };

  handleRemoveField = (index: number) => {
    if (!this._isMounted) return;

    this.setState((prevState) => ({
      fieldList: prevState.fieldList.filter((_, i) => i !== index)
    }));
  };

  handleFieldChange = (index: number, field: keyof IDataKeyTextField, value: string) => {
    if (!this._isMounted) return;

    this.setState((prevState) => ({
      fieldList: prevState.fieldList.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    }));
  };

  handleDragEnd = (result: DropResult) => {
    if (!this._isMounted) return;
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    this.setState((prevState) => {
      const newFieldList = Array.from(prevState.fieldList);
      const [removed] = newFieldList.splice(result.source.index, 1);
      newFieldList.splice(result.destination!.index, 0, removed);
      return { fieldList: newFieldList };
    });
  };

  render() {
    const { fieldList } = this.state;

    return (
      <div className="data-key-editor">
        <Form form={this.props.form} name="hereDataList" onFinish={this.handleFinish} autoComplete="off">
          <div className="data-key-editor__form">
            <div className="data-key-editor__field-list-title">
              <div className="kuika-flex-column--gap-l kuika-flex-center">
                <div className="data-key-editor__field-list-title--icon">
                  <KuikaSvg name="text-plus" width={32} height={32} />
                </div>
                <KuikaH5>Fixed Data</KuikaH5>
              </div>
              <KuikaText style={{ textAlign: "center" }}>
                Define static key-value pairs that will be available as fixed data options
              </KuikaText>
            </div>
            <DragDropContext onDragEnd={this.handleDragEnd}>
              <Droppable droppableId="data-key-field-list">
                {(provided) => (
                  <div className="data-key-editor__field-list" ref={provided.innerRef} {...provided.droppableProps}>
                    {fieldList.map((field, index) => (
                      <Draggable key={`field-${index}`} draggableId={`field-${index}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`data-key-editor__field-row ${
                              snapshot.isDragging ? "data-key-editor__field-row--dragging" : ""
                            }`}
                          >
                            <div {...provided.dragHandleProps} className="data-key-editor__drag-handle">
                              <KuikaSvg name="grip-vertical" width={12} height={12} color="var(--dark-5)" />
                            </div>
                            <KuikaText>Key</KuikaText>
                            <KuikaInput
                              size="md"
                              value={field.key}
                              onChange={(e) => this.handleFieldChange(index, "key", e.target.value)}
                              autoFocus={index === fieldList.length - 1}
                            />
                            <KuikaText>Text</KuikaText>
                            <KuikaInput
                              size="md"
                              value={field.text}
                              onChange={(e) => this.handleFieldChange(index, "text", e.target.value)}
                            />
                            <KuikaButton
                              className="data-key-editor__remove-button"
                              onClick={() => this.handleRemoveField(index)}
                              aria-label="Remove field"
                              isIcon
                              type="tertiary"
                            >
                              <KuikaSvg name="trash" width={16} height={16} color="var(--red-5)" />
                            </KuikaButton>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <div className="data-key-editor__footer">
            <KuikaButton type="text" size="md" onClick={this.handleAddField} className="data-key-editor__add-button">
              <KuikaSvg name="plus" width={16} height={16} />
              ADD FIELD
            </KuikaButton>
            <div className="kuika-flex-center--gap-m">
              <KuikaButton
                type="tertiary"
                size="md"
                onClick={() => {
                  if (this.props.handleClickOnCancel) {
                    this.props.handleClickOnCancel();
                  }
                }}
                color="neutral"
              >
                CANCEL
              </KuikaButton>
              <KuikaButton type="primary" size="md" htmlType="submit">
                UPDATE
              </KuikaButton>
            </div>
          </div>
        </Form>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    ) {
      return true;
    }
    return false;
  };
}

const dataKeyTextFieldList = withContext<IDataKeyTextFieldListProps>(
  WithForm<any>(withSD<IDataKeyTextFieldListProps>(DataKeyTextFieldList))
);
export { dataKeyTextFieldList as DataKeyTextFieldList };
